import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { DEFAULT_LOCALE, type Locale, SUPPORTED_LOCALES } from "~/i18n/resolveLocale";

import { displayCategoryName, displayName, extractExactI18nLabel } from "./i18nLabel";

const isSupported = (v: string): v is Locale => (SUPPORTED_LOCALES as readonly string[]).includes(v);

// Returns the resolved app-wide locale as a typed Locale (defaults to DEFAULT_LOCALE if unknown).
export const useDisplayLocale = (): Locale => {
  const { i18n } = useTranslation();
  const lng = i18n.resolvedLanguage ?? i18n.language;
  return isSupported(lng) ? lng : DEFAULT_LOCALE;
};

// Returns a stable callback that picks a locale-aware label from an i18nLabel object.

export const useExactI18nLabel = () => {
  const locale = useDisplayLocale();
  return useCallback(
    (label: Record<string, unknown> | null | undefined) => extractExactI18nLabel(label, locale),
    [locale],
  );
};

export const useDisplayCategoryName = () => {
  const locale = useDisplayLocale();
  return useCallback(
    (category: { name?: string | null; i18nLabel?: Record<string, unknown> | null } | null | undefined) =>
      displayCategoryName(category, locale),
    [locale],
  );
};

// Returns a stable callback that picks the right display name from a record carrying name/nameJa.
export const useDisplayName = () => {
  const locale = useDisplayLocale();
  return useCallback(
    (record: { name?: string | null; nameJa?: string | null } | null | undefined) => displayName(record, locale),
    [locale],
  );
};
